import React, { useCallback, useMemo, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { Form, Button, Row, Col, Card, Alert } from 'react-bootstrap';
import { PageHeader } from '../../components';
import useComponentState from '../../utils/useComponentState';
import styles from './AddVoucherThemes.module.scss';
import { toast } from 'react-toastify';
import { AddVoucherThemesApi } from '../../api/voucherTheme';
import {
  INITIAL_STATE,
  SUCCESS_MESSAGE,
  TOAST_MESSAGES,
  VOUCHER_THEME_FORM_FIELDS as FORM_FIELDS,
  validateForm
} from './AddVoucherThemesConstants';
import smsMessageTemplate from './templates/smsMessage.txt';
import emailMessageTemplate from './templates/emailMessage.txt';
import emailSubjectTemplate from './templates/emailSubject.txt';


// Utility functions
const createPayload = (state) => ({
  themeName: state.themeName,
  smsContent: state.smsMessage,
  emailContent: state.emailMessage,
  emailSubject: state.emailSubject,
  backgroundImageUrl: state.backgroundImageUrl,
  thumbnailImageUrl: state.thumbnailImageUrl,
  imageFrontUrl: state.frontImageUrl,
  imageBackUrl: state.backImageUrl,
  mobileImageUrl: state.mobileImageUrl,
});

const loadTemplates = async () => {
  try {
    const smsResponse = await fetch(smsMessageTemplate);
    const emailResponse = await fetch(emailMessageTemplate);
    const subjectResponse = await fetch(emailSubjectTemplate);

    const smsContent = await smsResponse.text();
    const emailContent = await emailResponse.text();
    const subjectContent = await subjectResponse.text();

    return {
      smsMessage: smsContent,
      emailMessage: emailContent,
      emailSubject: subjectContent,
    };
  } catch (error) {
    toast.error('Error loading templates:', error);
    return null;
  }
};

const resetFormState = (setState) => {
  setState({
    ...INITIAL_STATE,
    categories: [], // Preserve categories
  });
};

// FormField Component - Shows invalid state for required fields and character counter
const FormField = ({ field, value = '', onChange, fieldErrors }) => {
  const {
    name,
    label,
    type,
    placeholder,
    required,
    helpText,
    rows,
    maxLength,
  } = field;

  const isInvalid = !!fieldErrors[name];
  const currentLength = value.length;
  const isNearLimit =
    maxLength && currentLength >= Math.ceil(maxLength * 0.8) && currentLength < maxLength;
  const isAtLimit = maxLength && currentLength === maxLength;

  const controlClasses = `${styles.inputControl} ${isAtLimit ? styles['at-limit'] : ''} ${isNearLimit && !isAtLimit ? styles['near-limit'] : ''}`;
  const counterClasses = `${styles.charCounter} ${isAtLimit ? styles['at-limit'] : ''} ${isNearLimit && !isAtLimit ? styles['near-limit'] : ''}`;

  return (
    <Form.Group as={Row} className="mb-3">
      <Form.Label column sm={3}>
        {label} {required && <span className="text-danger">*</span>}
      </Form.Label>

      <Col sm={9}>
        <Form.Control
          as={type === 'textarea' ? 'textarea' : 'input'}
          type={type !== 'textarea' ? type : undefined}
          name={name}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          rows={rows}
          required={required}
          isInvalid={isInvalid || isAtLimit}
          maxLength={maxLength}
          className={controlClasses}
        />

        {/* Error (left) + Counter (right) */}
        <div className={styles.inputGrid}>
          {/* Validation error */}
          <div
            className={`invalid-feedback ${isInvalid ? 'd-block' : 'd-none'} ${styles.invalidFeedback}`}
          >
            {fieldErrors[name]}
          </div>

          {/* Character counter */}
          {maxLength && (
            <small className={counterClasses}>
              {currentLength}/{maxLength}
            </small>
          )}
        </div>

        {helpText && (
          <Form.Text className="text-muted">{helpText}</Form.Text>
        )}
      </Col>
    </Form.Group>
  );
};





// Main component
const AddVoucherThemes = ({ auth }) => {
  const [state, setState] = useComponentState({
    ...INITIAL_STATE,
    fieldErrors: {} // Track field-level errors 
  });

  const templatesLoadedRef = useRef(false);

  // Load templates on component mount (only once)
  useEffect(() => {
    if (templatesLoadedRef.current) return; // Skip if already loaded

    const initializeTemplates = async () => {
      const templates = await loadTemplates();
      if (templates) {
        setState(prev => ({
          ...prev,
          templates
        }));
        templatesLoadedRef.current = true;
      }
    };
    initializeTemplates();
  }, []); // Empty dependency array - runs only once on mount

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setState({
      [name]: value,
      error: '',
      fieldErrors: {} // Clear field errors when user starts typing
    });
  }, [setState]);

  // Load template content into form fields
  const handleLoadTemplate = useCallback(() => {
    if (state.templates) {
      setState({
        smsMessage: state.templates.smsMessage,
        emailMessage: state.templates.emailMessage,
        emailSubject: state.templates.emailSubject,
        fieldErrors: {}
      });
    }
  }, [state.templates, setState]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();

    // Run JS validation
    const validationErrors = validateForm(state);
    if (Object.keys(validationErrors).length > 0) {
      setState(prevState => ({
        ...prevState,
        fieldErrors: validationErrors
      }));
      e.stopPropagation();
      return;
    }

    // Clear errors and proceed
    setState(prevState => ({
      ...prevState,
      fieldErrors: {}
    }));

    setState({ loading: true, error: '' });

    try {
      const payload = createPayload(state);
      const response = await AddVoucherThemesApi(auth.auth.token, payload);

      if (response.message === SUCCESS_MESSAGE) {
        toast.success(TOAST_MESSAGES.success);
        resetFormState(setState);
        setState((prev) => ({ ...prev, categories: state.categories })); // Preserve categories
      } else {
        toast.error(TOAST_MESSAGES.error);
        setState({ loading: false });
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || TOAST_MESSAGES.error;
      setState({
        loading: false,
        error: errorMessage,
      });
      toast.error(errorMessage);
    }
  }, [state, setState, auth.auth.token]);

  const handleCancel = useCallback(() => {
    resetFormState(setState);
    setState((prev) => ({ ...prev, categories: state.categories })); // Preserve categories
  }, [setState, state.categories]);

  const formFields = useMemo(() =>
    FORM_FIELDS.map(field => (
      <FormField
        key={field.name}
        field={field}
        value={state[field.name]}
        onChange={handleInputChange}
        fieldErrors={state.fieldErrors}
      />
    )), [state, handleInputChange]
  );

  return (
    <div className={styles.root_div}>
      <PageHeader text='Add Voucher Theme' />

      <Card className={styles.formCard}>
        <Card.Body>
          {state.error && <Alert variant='danger'>{state.error}</Alert>}
          {state.success && (
            <Alert variant='success'>{TOAST_MESSAGES.success}</Alert>
          )}

          <Form noValidate onSubmit={handleSubmit}>
            <div className={`mb-3 ${styles.smallText}`}>
              <small>
                Fields marked with <span className='text-danger'>*</span> are required
              </small>
            </div>

            {/* Theme Name Field */}
            {formFields[0]}

            {/* Image URL Fields */}
            {formFields.slice(1, 6)}

            {/* Load Template Button */}
            <Form.Group as={Row} className='mb-3'>
              <Col sm={9} className='ms-auto'>
                <Button
                  variant='outline-primary'
                  size='sm'
                  onClick={handleLoadTemplate}
                  type='button'
                  title='Load default SMS, Email Subject, and Email Message templates'
                  centered
                >
                  Load Default Templates
                </Button>
              </Col>
            </Form.Group>

            {/* Email and SMS Fields */}
            {formFields.slice(6)}

            <div className={styles.formActions}>
              <Button variant='secondary' className='me-2' onClick={handleCancel}>
                Cancel
              </Button>
              <Button variant='primary' disabled={state.loading} type='submit'>
                {state.loading ? 'Adding...' : 'Add Theme'}
              </Button>
            </div>
          </Form>

        </Card.Body>
      </Card>
    </div>
  );
};

const mapStateToProps = (state) => ({
  auth: state.auth,
});

export default connect(mapStateToProps)(AddVoucherThemes);